import { useContext, useState, useEffect, useRef } from "react";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import swal from "sweetalert2";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import ConfiguratorRoot from "examples/Configurator/ConfiguratorRoot";
import TicketDetailView from "./TicketDetailView";

import { useSoftUIController, setOpenConfigurator, closePanels } from "context";

import AddSupportPopup from "utils/AddSupportPopup/AddSupportPopup";
import { api } from "utils/Api";
import AuthContext from "context/AuthContext";
import { getSecureItem } from "utils/SecureStorage";

import { useSearchParams, useParams } from "react-router-dom";
import { useHierarchy } from "context/HierarchyContext";
import { showSuccess } from "utils/messageAlerts";
import { canCreate } from "utils/permissionManager";

function Configurator() {
  const [searchParams] = useSearchParams();
  const [controller, dispatch] = useSoftUIController();
  const { openConfigurator } = controller;
  const [tickets, setTickets] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const organization_id = JSON.parse(getSecureItem("currentUser"))?.organization_id;
  const user_id = JSON.parse(getSecureItem("currentUser"))?.id;
  const [units, setUnits] = useState([]);
  const userRole = getSecureItem("role_id");

  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitDropdownOpen, setUnitDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedUnitObj = units.find((u) => u.id === selectedUnit);
  const [ticketStatuses, setTicketStatuses] = useState([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);

  const params = useParams();

  const { propertyId, setPropertyId, orgId } = useHierarchy();

  /* ── Whether we show the detail view ── */
  const showDetail = Boolean(selectedTicket);

  const configuratorRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setUnitDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        openConfigurator &&
        configuratorRef.current &&
        !configuratorRef.current.contains(event.target)
      ) {
        closePanels(dispatch);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [openConfigurator]);

  // Resolve property from code
  const resolvePropertyId = async () => {
    // 1️⃣ If already in hierarchy → use it
    if (propertyId) return propertyId;

    // 2️⃣ Get propertyCode from path OR query
    const propertyCode = params.propertyCode || searchParams.get("property_code") || null;
    console.log('propertyCode: ', propertyCode);

    if (!propertyCode) return null;

    try {
      const properties = await api.properties.getProperties();

      const match = properties.find((p) => p.property_code === propertyCode);
      console.log('match: ', match);

      if (match) {
        setPropertyId(match.id); // store globally
        return match.id;
      }

      return null;
    } catch (error) {}
  };

  useEffect(() => {
    const init = async () => {
      if (!openConfigurator) return;

      const activePropertyId = await resolvePropertyId();
      if (!activePropertyId) return;

      setSelectedUnit(null);
      setSelectedTicket(null);
      setTickets([]);
      fetchUnits(activePropertyId);
    };

    init();
  }, [openConfigurator]);

  useEffect(() => {
    if (openConfigurator) {
      fetchTicketStatus();
    }
  }, [openConfigurator]);

  // Fetch tickets
  const fetchTickets = async (activePropertyId, unitId) => {
    try {
      if (!activePropertyId || !unitId) return;
      const payload = {
        property_id: activePropertyId,
        organization_id : orgId || organization_id,
        unit_id: unitId,
      };

      // ✅ Only pass user_id when role is 5
      if (Number(userRole) === 5) {
        payload.user_id = user_id;
      }

      const response = await api.ticket.getFilteredTickets(payload);
      setTickets(response || []);
    } catch {}
  };

  const fetchTicketStatus = async () => {
    try {
      const response = await api.messages.getTicketStatus();
      setTicketStatuses(response || []);
    } catch (error) {}
  };

  const handleStatusChange = async (ticketId, statusId) => {
    try {
      await api.ticket.updateTicketDetails(ticketId, { ticket_status_id: statusId , organization_id: orgId || organization_id });

      showSuccess("Ticket status updated");

      // refresh tickets
      if (propertyId && selectedUnit) {
        fetchTickets(propertyId, selectedUnit);
      }

      setStatusDropdownOpen(null);
    } catch (error) {}
  };

  // Fetch units
  const fetchUnits = async (activePropertyId) => {
    try {
      if (!activePropertyId) return;
      const payload = { property_id: activePropertyId, org_id: orgId || organization_id, role_id : userRole };
      const res = await api.multiOrgProperty.postMultiOrgUnits(payload)
      const filteredResponse = res.filter((a) => a.active)
      setUnits(filteredResponse || []);
    } catch (error) {}
  };

  // On configurator open
  useEffect(() => {
    if (openConfigurator && propertyId) {
      setSelectedUnit(null);
      setSelectedTicket(null); // reset detail view
      setTickets([]);
      fetchUnits(propertyId);
    }
  }, [openConfigurator, propertyId]);

  // Fetch tickets when unit selected
  useEffect(() => {
    if (!selectedUnit) {
      setTickets([]);
      return;
    }
    if (propertyId && selectedUnit) {
      fetchTickets(propertyId, selectedUnit);
    }
  }, [selectedUnit, propertyId]);

  const handleUnitSelect = (unitId) => {
    setSelectedUnit(unitId);
    setUnitDropdownOpen(false);
  };

  // Create ticket
  const handleCreateTicket = async (payload) => {
    try {
      await api.ticket.createTicket(payload);
      showSuccess("Ticket Created Successfully");
      setIsPopupOpen(false);
      if (selectedUnit) fetchTickets(propertyId, selectedUnit);
    } catch {}
  };

  /* ── Back from detail → list (re-fetch to catch updates) ── */
  const handleBackToList = () => {
    setSelectedTicket(null);
    if (propertyId && selectedUnit) {
      fetchTickets(propertyId, selectedUnit);
    }
  };

  const handleCloseConfigurator = () => closePanels(dispatch);

  /* ══════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════ */
  return (
    <>
      <ConfiguratorRoot ref={configuratorRef} variant="permanent" ownerState={{ openConfigurator }}>
        <SoftBox display="flex" flexDirection="column" sx={{ height: "100%" }}>
          {/* ──────────────────────────────────
              DETAIL VIEW (ticket selected)
              ────────────────────────────────── */}
          {showDetail ? (
            <TicketDetailView
              ticket={selectedTicket}
              onBack={handleBackToList}
              currentUserId={user_id}
              ticketStatuses={ticketStatuses}
            />
          ) : (
            <>
              {/* LIST VIEW HEADER */}
              <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                pt={1}
                pb={0.8}
                px={2}
              >
                <SoftTypography variant="h5">Support Tickets</SoftTypography>

                <SoftBox display="flex" alignItems="center" gap={1}>
                  {canCreate("Ticket") && (
                    <SoftButton
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={() => setIsPopupOpen(true)}
                    >
                      Add Ticket
                    </SoftButton>
                  )}

                  <Icon sx={{ cursor: "pointer" }} onClick={handleCloseConfigurator}>
                    close
                  </Icon>
                </SoftBox>
              </SoftBox>

              <Divider sx={{ my: 0 }} />

              {/* ── UNIT FILTER ── */}
              <SoftBox px={2} pt={1.5} pb={1}>
                <SoftTypography
                  variant="caption"
                  fontWeight="bold"
                  sx={{ mb: 0.5, display: "block" }}
                >
                  Select Unit
                </SoftTypography>

                <SoftBox ref={dropdownRef} sx={{ position: "relative" }}>
                  {/* Trigger */}
                  <SoftBox
                    onClick={() => setUnitDropdownOpen((prev) => !prev)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      border: "1px solid",
                      borderColor: unitDropdownOpen ? "#17c1e8" : "#d2d6da",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      cursor: "pointer",
                      minHeight: "40px",
                      transition: "all 0.2s ease",
                      boxShadow: unitDropdownOpen ? "0 0 0 2px rgba(23,193,232,0.15)" : "none",
                      "&:hover": { borderColor: "#17c1e8" },
                    }}
                  >
                    <SoftTypography
                      variant="button"
                      sx={{
                        color: selectedUnitObj ? "#344767" : "#adb5bd",
                        fontWeight: selectedUnitObj ? 500 : 400,
                        fontSize: "0.875rem",
                      }}
                    >
                      {selectedUnitObj ? selectedUnitObj.name : "Please select a unit..."}
                    </SoftTypography>

                    <KeyboardArrowDownIcon
                      sx={{
                        fontSize: 20,
                        color: "#8392ab",
                        transition: "transform 0.2s ease",
                        transform: unitDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </SoftBox>

                  {/* Dropdown */}
                  {unitDropdownOpen && (
                    <SoftBox
                      sx={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        right: 0,
                        backgroundColor: "#fff",
                        border: "1px solid #e9ecef",
                        borderRadius: "8px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                        zIndex: 50,
                        maxHeight: "200px",
                        overflowY: "auto",
                        "&::-webkit-scrollbar": { width: "6px" },
                        "&::-webkit-scrollbar-track": {
                          background: "#f8f9fa",
                          borderRadius: "0 8px 8px 0",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "#d2d6da",
                          borderRadius: "3px",
                        },
                      }}
                    >
                      {units.length === 0 ? (
                        <SoftBox sx={{ p: 2, textAlign: "center" }}>
                          <SoftTypography variant="caption" sx={{ color: "#adb5bd" }}>
                            No units available
                          </SoftTypography>
                        </SoftBox>
                      ) : (
                        units.map((unit) => {
                          const isSelected = selectedUnit === unit.id;
                          return (
                            <SoftBox
                              key={unit.id}
                              onClick={() => handleUnitSelect(unit.id)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "10px 14px",
                                cursor: "pointer",
                                backgroundColor: isSelected ? "#e8f7fc" : "transparent",
                                transition: "all 0.15s ease",
                                "&:hover": {
                                  backgroundColor: isSelected ? "#daf1f9" : "#f8f9fa",
                                },
                                "&:first-of-type": {
                                  borderRadius: "8px 8px 0 0",
                                },
                                "&:last-of-type": {
                                  borderRadius: "0 0 8px 8px",
                                },
                              }}
                            >
                              <SoftTypography
                                variant="button"
                                sx={{
                                  fontWeight: isSelected ? 600 : 400,
                                  color: isSelected ? "#17c1e8" : "#344767",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {unit.name}
                              </SoftTypography>

                              {isSelected && <CheckIcon sx={{ fontSize: 18, color: "#17c1e8" }} />}
                            </SoftBox>
                          );
                        })
                      )}
                    </SoftBox>
                  )}
                </SoftBox>
              </SoftBox>

              <Divider sx={{ my: 0 }} />

              {/* ── TICKET LIST ── */}
              <SoftBox
                pt={1}
                pb={2}
                px={2}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-track": { background: "#f8f9fa" },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#d2d6da",
                    borderRadius: "3px",
                  },
                }}
              >
                {/* No unit */}
                {!selectedUnit && (
                  <SoftBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ mt: 6 }}
                  >
                    <Icon sx={{ fontSize: 48, color: "#d2d6da", mb: 1 }}>apartment</Icon>
                    <SoftTypography variant="body2" sx={{ color: "#adb5bd", textAlign: "center" }}>
                      Please select a unit to view tickets.
                    </SoftTypography>
                  </SoftBox>
                )}

                {/* Unit selected, no tickets */}
                {selectedUnit && tickets.length === 0 && (
                  <SoftBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ mt: 6 }}
                  >
                    <Icon sx={{ fontSize: 48, color: "#d2d6da", mb: 1 }}>confirmation_number</Icon>
                    <SoftTypography variant="body2" sx={{ color: "#adb5bd", textAlign: "center" }}>
                      No tickets found for this unit.
                    </SoftTypography>
                  </SoftBox>
                )}

                {/* Ticket cards */}
                {selectedUnit &&
                  tickets.map((ticket) => {
                    const statusConfig = {
                      open: {
                        color: "#e67e22",
                        bg: "#fef5e7",
                        border: "#fdebd0",
                        icon: "radio_button_checked",
                      },
                      in_process: {
                        color: "#3498db",
                        bg: "#eaf2f8",
                        border: "#d4e6f1",
                        icon: "autorenew",
                      },
                      closed: {
                        color: "#95a5a6",
                        bg: "#f4f6f6",
                        border: "#e5e8e8",
                        icon: "cancel",
                      },
                    };

                    const normalizedStatus = ticket.ticket_status.name
                      .toLowerCase()
                      .replace(/\s+/g, "_");

                    const status = statusConfig[normalizedStatus] || statusConfig.open;

                    const createdDate = new Date(ticket.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    });

                    const createdTime = new Date(ticket.created_at).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                    });

                    return (
                      <SoftBox
                        key={ticket.id}
                        // onClick={() => setSelectedTicket(ticket)}
                        sx={{
                          mb: 1.5,
                          p: 2,
                          borderRadius: "12px",
                          border: "1px solid #e9ecef",
                          backgroundColor: "#fff",
                          // cursor: statusDropdownOpen === ticket.id ? "default" : "pointer",
                          transition: "all 0.2s ease",

                          ...(statusDropdownOpen !== ticket.id && {
                            "&:hover": {
                              borderColor: "#17c1e8",
                              boxShadow: "0 4px 12px rgba(23,193,232,0.1)",
                              transform: "translateY(-1px)",
                            },
                          }),
                        }}
                      >
                        {/* Subject + Status */}
                        <SoftBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          mb={1}
                        >
                          <SoftTypography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: "#344767",
                              fontSize: "0.9rem",
                              flex: 1,
                              mr: 1,
                            }}
                          >
                            {ticket.subject}
                          </SoftTypography>

                          {/* <SoftBox
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                              px: 1.2,
                              py: 0.3,
                              borderRadius: "6px",
                              backgroundColor: status.bg,
                              border: `1px solid ${status.border}`,
                              flexShrink: 0,
                            }}
                          >
                            <Icon
                              sx={{
                                fontSize: 14,
                                color: status.color,
                              }}
                            >
                              {status.icon}
                            </Icon>
                            <SoftTypography
                              variant="caption"
                              sx={{
                                color: status.color,
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                textTransform: "capitalize",
                              }}
                            >
                              {ticket.ticket_status.name.replace("_", " ")}
                            </SoftTypography>
                          </SoftBox> */}
                          <SoftBox sx={{ position: "relative" }}>
                            {/* STATUS BUTTON */}
                            <SoftBox
                              onClick={(e) => {
                                e.stopPropagation();

                                if (Number(userRole) !== 5) {
                                  setStatusDropdownOpen(
                                    statusDropdownOpen === ticket.id ? null : ticket.id
                                  );
                                }
                              }}
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              sx={{
                                px: 1.2,
                                py: 0.3,
                                borderRadius: "6px",
                                backgroundColor: status.bg,
                                border: `1px solid ${status.border}`,
                                cursor: Number(userRole) === 5 ? "default" : "pointer",
                              }}
                            >
                              <Icon sx={{ fontSize: 14, color: status.color }}>{status.icon}</Icon>
                              <SoftTypography
                                variant="caption"
                                sx={{
                                  color: status.color,
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                }}
                              >
                                {ticket.ticket_status.name}
                              </SoftTypography>
                              {Number(userRole) !== 5 && (
                                <span className="material-icons" style={{ fontSize: 16 }}>
                                  keyboard_arrow_down
                                </span>
                              )}
                            </SoftBox>

                            {/* DROPDOWN */}
                            {statusDropdownOpen === ticket.id && (
                              <SoftBox
                                sx={{
                                  position: "absolute",
                                  top: "110%",
                                  right: 0,
                                  backgroundColor: "#fff",
                                  border: "1px solid #e9ecef",
                                  borderRadius: "8px",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                  zIndex: 999,
                                  minWidth: "130px",
                                }}
                              >
                                {ticketStatuses.map((statusOption) => {
                                  const isActive = statusOption.id === ticket.ticket_status?.id;

                                  return (
                                    <SoftBox
                                      key={statusOption.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStatusChange(ticket.id, statusOption.id);
                                      }}
                                      sx={{
                                        px: 2,
                                        py: 1,
                                        cursor: "pointer",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        backgroundColor: isActive ? "#e8f7fc" : "transparent",
                                        fontWeight: isActive ? 600 : 400,
                                        "&:hover": { backgroundColor: "#f8f9fa" },
                                      }}
                                    >
                                      <SoftTypography
                                        variant="caption"
                                        sx={{
                                          color: isActive ? "#17c1e8" : "#344767",
                                          fontWeight: isActive ? 600 : 400,
                                        }}
                                      >
                                        {statusOption.name}
                                      </SoftTypography>

                                      {isActive && (
                                        <CheckIcon sx={{ fontSize: 16, color: "#17c1e8" }} />
                                      )}
                                    </SoftBox>
                                  );
                                })}
                              </SoftBox>
                            )}
                          </SoftBox>
                        </SoftBox>

                        {/* Description */}
                        <SoftTypography
                          variant="body2"
                          sx={{
                            color: "#67748e",
                            fontSize: "0.8rem",
                            lineHeight: 1.5,
                            mb: 1.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {ticket.description}
                        </SoftTypography>

                        {/* Footer */}
                        <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                          <SoftBox display="flex" alignItems="center" gap={0.5}>
                            <Icon sx={{ fontSize: 14, color: "#adb5bd" }}>confirmation_number</Icon>
                            <SoftTypography
                              variant="caption"
                              sx={{
                                color: "#adb5bd",
                                fontWeight: 500,
                                fontSize: "0.72rem",
                              }}
                            >
                              TKT-{String(ticket.id).padStart(4, "0")}
                            </SoftTypography>
                          </SoftBox>

                          <SoftBox display="flex" alignItems="center" gap={0.5}>
                            <Icon sx={{ fontSize: 14, color: "#adb5bd" }}>schedule</Icon>
                            <SoftTypography
                              variant="caption"
                              sx={{
                                color: "#adb5bd",
                                fontWeight: 500,
                                fontSize: "0.72rem",
                              }}
                            >
                              {createdDate} · {createdTime}
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                    );
                  })}
              </SoftBox>
            </>
          )}
        </SoftBox>

        {/* CREATE POPUP (unchanged) */}
        <AddSupportPopup
          open={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          onSubmit={handleCreateTicket}
          organization_id={orgId || organization_id}
          property_id={propertyId}
          user_id={user_id}
          units={units}
          userRole={userRole}
        />
      </ConfiguratorRoot>
    </>
  );
}

export default Configurator;